import { HomeContent } from '../seo/PublicContent';
import { intro } from '../seo/content';
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  CameraIcon,
  PhotoIcon,
  ClockIcon,
  StarIcon,
  ChartBarIcon,
  SparklesIcon,
  VideoCameraIcon,
  PlayIcon,
  RocketLaunchIcon,
  EyeIcon,
  BoltIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../hooks/useAuth';
import { useStats } from '../hooks/useStats';
import { useNewTour } from '../contexts/NewTourContext';
import { StatsSkeleton, ListSkeleton } from '../components/Skeleton';
import PullToRefresh from '../components/PullToRefresh';

import { useRecentActivity } from '../hooks/useRecentActivity';
import { formatDistanceToNow } from 'date-fns';
import ChangelogBoard from '../components/ChangelogBoard';

const HomePage: React.FC = () => {
  const { user } = useAuth();
  const { stats, isLoading: statsLoading, refreshStats } = useStats();
  const { startTour } = useNewTour();
  const { recentActivity, isLoading: activityLoading } = useRecentActivity();
  const [animationPhase, setAnimationPhase] = useState(0);

  const handleStartTour = () => {
    console.log('Starting tour...');
    startTour();
  };

  const handleRefresh = async () => {
    refreshStats();
  };

  // Animation cycle for the hero section
  useEffect(() => {
    const interval = setInterval(() => {
      setAnimationPhase((prev) => (prev + 1) % 4);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  const features = [
    {
      name: 'Quick Scan',
      description: 'Instantly analyze items with your camera',
      icon: CameraIcon,
      href: '/analyze',
      'data-tour': 'tour-quick-scan',
      color: 'from-blue-500 to-cyan-500',
    },
    {
      name: 'Upload Photo',
      description: 'Select from your photo library',
      icon: PhotoIcon,
      href: '/analyze',
      color: 'from-green-500 to-emerald-500',
    },
    {
      name: 'AI Analysis',
      description: 'Identify items and check matching live eBay listings',
      icon: SparklesIcon,
      href: '/analyze',
      'data-tour': 'tour-ai-analysis',
      color: 'from-purple-500 to-pink-500',
    },
    {
      name: 'Image Enhancements',
      description: 'Auto-rotation, background removal, dimensions',
      icon: RocketLaunchIcon,
      href: '/demo/enhancements',
      color: 'from-orange-500 to-red-500',
    },
    {
      name: 'Recent Scans',
      description: 'View your analysis history',
      icon: ClockIcon,
      href: '/history',
      color: 'from-amber-500 to-yellow-500',
    },
    {
      name: 'Favorites',
      description: 'Access your favorite analyses',
      icon: StarIcon,
      href: '/favorites',
      color: 'from-rose-500 to-pink-500',
    },
    {
      name: 'Analytics',
      description: 'Track your scanning activity',
      icon: ChartBarIcon,
      href: '/analytics',
      color: 'from-indigo-500 to-blue-500',
    },
    {
      name: 'Live Video Analysis',
      description: 'Analyze items instantly from your webcam',
      icon: VideoCameraIcon,
      href: '/live-analysis',
      color: 'from-violet-500 to-purple-500',
    },
  ];

  // Transform real stats into the format expected by the UI
  const statsData = [
    { 
      label: 'Total Scans', 
      value: statsLoading ? '...' : stats.totalScans.toString(),
      icon: EyeIcon,
      color: 'from-blue-500 to-cyan-500'
    },
    { 
      label: 'Favorites', 
      value: statsLoading ? '...' : stats.savedItems.toString(),
      icon: StarIcon,
      color: 'from-purple-500 to-pink-500'
    },
    { 
      label: 'This Month', 
      value: statsLoading ? '...' : stats.thisMonth.toString(),
      icon: BoltIcon,
      color: 'from-green-500 to-emerald-500'
    },
  ];

  const formatTimestamp = (timestamp: number) => {
    return formatDistanceToNow(new Date(timestamp), { addSuffix: true });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 dark:from-slate-900 dark:via-blue-900 dark:to-indigo-900 relative overflow-hidden">
      {/* Animated Background Elements */}
      <div className="absolute inset-0 pointer-events-none">
        {/* Floating Orbs */}
        <div className="absolute top-20 left-10 w-64 h-64 bg-gradient-to-r from-blue-400/20 to-purple-400/20 rounded-full blur-3xl animate-float" />
        <div className="absolute top-40 right-20 w-48 h-48 bg-gradient-to-r from-pink-400/20 to-orange-400/20 rounded-full blur-3xl animate-float-gentle" />
        <div className="absolute bottom-32 left-1/4 w-56 h-56 bg-gradient-to-r from-green-400/20 to-cyan-400/20 rounded-full blur-3xl animate-pulse" />
        
        {/* Grid Pattern */}
        <div className="absolute inset-0 bg-[linear-gradient(rgba(59,130,246,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(59,130,246,0.03)_1px,transparent_1px)] bg-[size:40px_40px]" />
      </div>

      <PullToRefresh onRefresh={handleRefresh}>
        <div className="relative z-10 max-w-7xl mx-auto px-4 py-8 space-y-16">
          {/* Hero Section with Video Background */}
          <div className="text-center relative overflow-hidden rounded-3xl" data-tour="home-header">
            {/* Video Background */}
            <div className="absolute inset-0 z-0">
              <video 
                autoPlay 
                muted 
                loop 
                playsInline
                className="absolute inset-0 w-full h-full object-cover"
                style={{ filter: 'brightness(0.3) blur(1px)' }}
              >
                <source src="/snapalyze video.mp4" type="video/mp4" />
              </video>
              {/* Gradient overlay for better text readability */}
              <div className="absolute inset-0 bg-gradient-to-br from-black/60 via-blue-900/40 to-purple-900/60" />
            </div>

            {/* Hero Content */}
            <div className="relative z-10 py-16 px-8">
            <div className="relative inline-block mb-8">
              {/* Main Logo - No Box, Bigger Size with Effects */}
              <div className="relative">
                <img 
                  src="/thriftalyze-logo.png"
                  alt="Thriftalyze Logo"
                  className="w-32 h-32 md:w-48 md:h-48 object-contain rounded-3xl transform transition-all duration-1000 hover:scale-110 hover:rotate-6 animate-float drop-shadow-2xl"
                />
                
                {/* Glowing effect behind logo */}
                <div className="absolute inset-0 w-32 h-32 md:w-48 md:h-48 bg-gradient-to-r from-blue-500/30 via-purple-500/30 to-pink-500/30 rounded-full blur-3xl animate-pulse -z-10" />
                
                {/* Orbiting sparkle effects */}
                <div className="absolute inset-0 w-32 h-32 md:w-48 md:h-48">
                  <div className={`absolute inset-0 transition-transform duration-3000 ${animationPhase === 0 ? 'rotate-0' : animationPhase === 1 ? 'rotate-90' : animationPhase === 2 ? 'rotate-180' : 'rotate-270'}`}>
                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 w-3 h-3 bg-gradient-to-r from-yellow-400 to-orange-400 rounded-full shadow-lg animate-bounce opacity-80" />
                    <div className="absolute top-1/2 -right-4 transform -translate-y-1/2 w-2 h-2 bg-gradient-to-r from-green-400 to-emerald-400 rounded-full shadow-lg animate-ping opacity-80" />
                    <div className="absolute -bottom-4 left-1/2 transform -translate-x-1/2 w-4 h-4 bg-gradient-to-r from-pink-400 to-rose-400 rounded-full shadow-lg animate-pulse opacity-80" />
                    <div className="absolute top-1/2 -left-4 transform -translate-y-1/2 w-2 h-2 bg-gradient-to-r from-cyan-400 to-blue-400 rounded-full shadow-lg animate-bounce opacity-80" />
                    
                    {/* Additional sparkles */}
                    <div className="absolute top-8 right-8 w-1 h-1 bg-white rounded-full animate-ping opacity-60" />
                    <div className="absolute bottom-8 left-8 w-1 h-1 bg-white rounded-full animate-pulse opacity-60" />
                    <div className="absolute top-16 left-16 w-1 h-1 bg-white rounded-full animate-bounce opacity-60" />
                  </div>
                </div>
                
                {/* Shimmer effect overlay */}
                <div className="absolute inset-0 w-32 h-32 md:w-48 md:h-48 overflow-hidden rounded-full opacity-20">
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/50 to-transparent transform -skew-x-12 -translate-x-full animate-shimmer" />
                </div>
              </div>
            </div>

            <div className="space-y-6">
              <h1 className="text-4xl md:text-6xl font-bold bg-gradient-to-r from-gray-900 via-blue-800 to-purple-800 dark:from-white dark:via-blue-200 dark:to-purple-200 bg-clip-text text-transparent leading-tight">
                Know What Your Thrift Store Finds Are Worth
              </h1>
              
              <p className="text-xl md:text-2xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto leading-relaxed">
                {intro}
              </p>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mt-8">
                <button
                  onClick={handleStartTour}
                  className="group relative inline-flex items-center px-8 py-4 bg-gradient-to-r from-purple-600 via-blue-600 to-cyan-600 hover:from-purple-700 hover:via-blue-700 hover:to-cyan-700 text-white font-bold rounded-2xl shadow-2xl hover:shadow-purple-500/25 transform hover:scale-105 transition-all duration-300 space-x-3 overflow-hidden"
                  data-tour="tour-start-button"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-purple-600 to-blue-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  <PlayIcon className="h-6 w-6 relative z-10 group-hover:animate-pulse" />
                  <span className="relative z-10 text-lg">Take Interactive Tour</span>
                  <div className="absolute -inset-1 bg-gradient-to-r from-purple-600 to-blue-600 rounded-2xl blur opacity-30 group-hover:opacity-100 transition duration-300" />
                </button>
                
                <Link
                  to="/analyze"
                  className="group inline-flex items-center px-8 py-4 bg-white dark:bg-gray-800 text-gray-900 dark:text-white font-semibold rounded-2xl shadow-xl hover:shadow-2xl border border-gray-200 dark:border-gray-700 hover:border-purple-300 dark:hover:border-purple-500 transform hover:scale-105 transition-all duration-300 space-x-3"
                  data-tour="tour-start-analyzing"
                >
                  <CameraIcon className="h-6 w-6 group-hover:animate-bounce" />
                  <span className="text-lg">Start Analyzing</span>
                </Link>
              </div>
            </div>
            </div>
          </div>

          {/* Stats Cards */}
          {statsLoading ? (
            <StatsSkeleton />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {statsData.map((stat, index) => (
                <div
                  key={stat.label}
                  className="group relative bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-3xl p-8 shadow-xl border border-gray-200/50 dark:border-gray-700/50 hover:shadow-2xl transform hover:scale-105 transition-all duration-500 overflow-hidden"
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  {/* Background Gradient */}
                  <div className={`absolute inset-0 bg-gradient-to-br ${stat.color} opacity-0 group-hover:opacity-10 transition-opacity duration-300`} />
                  
                  <div className="relative z-10 text-center space-y-4">
                    <div className={`inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r ${stat.color} rounded-2xl shadow-lg mb-4`}>
                      <stat.icon className="h-8 w-8 text-white" />
                    </div>
                    
                    <div className={`text-4xl md:text-5xl font-bold bg-gradient-to-r ${stat.color} bg-clip-text text-transparent`}>
                      {stat.value}
                    </div>
                    
                    <div className="text-lg font-medium text-gray-600 dark:text-gray-400">
                      {stat.label}
                    </div>
                  </div>
                  
                  {/* Hover Effect */}
                  <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="absolute inset-0 bg-gradient-to-r from-white/10 to-transparent rounded-3xl" />
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Changelog Board */}
          <div className="animate-fadeIn" style={{ animationDelay: '400ms' }}>
            <ChangelogBoard maxEntries={3} />
          </div>

          {/* Quick Actions */}
          <div className="space-y-12">
            <div className="text-center">
              <h2 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 dark:from-white dark:to-gray-300 bg-clip-text text-transparent mb-4">
                Quick Actions
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
                Choose your preferred way to analyze and discover valuable items
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {features.map((feature, index) => (
                <Link
                  key={feature.name}
                  to={feature.href}
                  className="group relative touch-target"
                  data-tour={feature['data-tour']}
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <div className="relative bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-3xl p-6 shadow-xl border border-gray-200/50 dark:border-gray-700/50 hover:shadow-2xl transform hover:scale-105 hover:-translate-y-2 transition-all duration-500 overflow-hidden animate-fadeIn">
                    {/* Background Gradient on Hover */}
                    <div className={`absolute inset-0 bg-gradient-to-br ${feature.color} opacity-0 group-hover:opacity-10 transition-opacity duration-300`} />
                    
                    <div className="relative z-10 space-y-4">
                      <div className={`inline-flex items-center justify-center w-14 h-14 bg-gradient-to-r ${feature.color} rounded-2xl shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                        <feature.icon className="h-7 w-7 text-white group-hover:animate-pulse" />
                      </div>
                      
                      <div className="space-y-2">
                        <h3 className="text-lg font-bold text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors duration-300">
                          {feature.name}
                        </h3>
                        <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                          {feature.description}
                        </p>
                      </div>
                    </div>
                    
                    {/* Shine Effect */}
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
                  </div>
                </Link>
              ))}
            </div>
          </div>

          {/* Recent Activity */}
          <div className="space-y-8">
            <div className="text-center">
              <h2 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 dark:from-white dark:to-gray-300 bg-clip-text text-transparent mb-4">
                Recent Activity
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-400">
                Track your latest discoveries and analyses
              </p>
            </div>
            
            <div className="relative bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-3xl shadow-xl border border-gray-200/50 dark:border-gray-700/50 overflow-hidden">
              {activityLoading ? (
                <div className="p-8">
                  <ListSkeleton items={3} />
                </div>
              ) : recentActivity.length > 0 ? (
                <div className="divide-y divide-gray-200/50 dark:divide-gray-700/50">
                  {recentActivity.map((item, index) => (
                    <div 
                      key={item.id} 
                      className="p-6 hover:bg-gradient-to-r hover:from-blue-50/50 hover:to-purple-50/50 dark:hover:from-blue-900/20 dark:hover:to-purple-900/20 transition-all duration-300 group"
                      style={{ animationDelay: `${index * 100}ms` }}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0 space-y-2">
                          <h3 className="text-lg font-semibold text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors duration-300">
                            {item.title}
                          </h3>
                          <p className="text-sm text-gray-600 dark:text-gray-400 flex items-center space-x-2">
                            <ClockIcon className="h-4 w-4" />
                            <span>{formatTimestamp(item.timestamp)}</span>
                          </p>
                        </div>
                        <div className="text-2xl font-bold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent ml-6 group-hover:scale-110 transition-transform duration-300">
                          {item.value}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-16 text-center">
                  <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-r from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-600 rounded-3xl mb-6">
                    <ClockIcon className="h-10 w-10 text-gray-400 animate-pulse" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">No recent activity</h3>
                  <p className="text-gray-600 dark:text-gray-400 mb-6">Start analyzing items to see your activity here</p>
                  <Link
                    to="/analyze"
                    className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 space-x-2"
                  >
                    <CameraIcon className="h-5 w-5" />
                    <span>Start Your First Analysis</span>
                  </Link>
                </div>
              )}
              
              {recentActivity.length > 0 && (
                <div className="bg-gradient-to-r from-gray-50 to-blue-50 dark:from-gray-700/50 dark:to-blue-900/20 px-8 py-6">
                  <Link
                    to="/history"
                    className="inline-flex items-center text-lg font-semibold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent hover:from-blue-700 hover:to-purple-700 transition-all duration-300 space-x-2 group"
                  >
                    <span>View all activity</span>
                    <span className="transform group-hover:translate-x-1 transition-transform duration-300">→</span>
                  </Link>
                </div>
              )}
            </div>
          </div>
          <div className="rounded-3xl bg-slate-900 p-6 md:p-10"><HomeContent /></div>
        </div>
      </PullToRefresh>
    </div>
  );
};

export default HomePage;
